import {
  Dialog,
  DialogHeader,
  DialogBody,
  DialogFooter,
  Typography,
  Input,
} from "@material-tailwind/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import Select from "react-select";
import { useForm, Controller } from "react-hook-form";
import { fetchCountry, fetchCity } from "@/store/slices/internalUsersSlice";
import { fetchDeliveryPartners } from "@/store/slices/orderSlice";
import DeliveryLocationSearch from "@/components/DeliveryLocationSearch";
import { useTranslation } from "react-i18next";

const customSelectStyles = {
  control: (provided, state) => ({
    ...provided,
    borderRadius: "8px",
    minHeight: "50px",
    borderColor: state.isFocused ? "#212121" : "#CFD2D9",
    boxShadow: state.isFocused ? "0 0 0 1px #212121" : "none",
    "&:hover": { borderColor: "#CFD2D9" },
    backgroundColor: "#F3F4F6", // Consistent with read-only UI
    cursor: "not-allowed",
  }),
  singleValue: (provided) => ({ ...provided, color: "#111827", fontSize: "14px" }),
  placeholder: (provided) => ({ ...provided, color: "#9CA3AF", fontSize: "14px" }),
  menu: (provided) => ({ ...provided, zIndex: 50 }),
};

const priorities = [
  { label: "High", value: "high" },
  { label: "Medium", value: "medium" },
  { label: "Low", value: "low" },
];

function formatDateTimeLocal(date) {
  if (!date) return "";
  const d = new Date(date);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}`;
}

export default function ViewAssignedDeliveryAgentModal({
  onClose,
  itemId,
  customer,
  rfq_product_id,
  vendor,
  initialData = null,
}) {
  const dispatch = useDispatch();
  const { t } = useTranslation('order');
  const maxQty = initialData?.quantity ?? vendor?.product_quantity ?? 1;

  const countries = useSelector((state) => state.internalUser?.countries || []);
  const cities = useSelector((state) => state.internalUser?.cities || []);
  const { delivery_partners = [] } = useSelector((state) => state.order);

  const vendorCountryId = useMemo(
    () => initialData?.location?.country_id ?? initialData?.country_id ?? "",
    [initialData]
  );
  const vendorCityId = useMemo(
    () => initialData?.location?.city_id ?? initialData?.city_id ?? "",
    [initialData]
  );

  const {
    register,
    watch,
    control,
    reset,
    setValue,
  } = useForm({
    defaultValues: {
      country_id: vendorCountryId,
      city_id: vendorCityId,
      delivery_agent_id: initialData?.assigned_to,
      quantity: maxQty,
      amount_deducted_reason: initialData?.amount_deducted_reason,
      amount_deducted: initialData?.amount_deducted,
      delivery_charge: initialData?.delivery_charge ?? null,
      delivery_charge_paid_amount: initialData?.delivery_charge_paid_amount ?? null,
      cash_amount: initialData?.cash_amount ?? null,
      deadline: initialData?.deadline ? formatDateTimeLocal(initialData.deadline) : null,
      priority: initialData?.priority ?? "medium",
      delivery_address: initialData?.delivery_address ?? "",
      delivery_landmark: initialData?.delivery_landmark ?? "",
      latitude: initialData?.latitude ?? null,
      longitude: initialData?.longitude ?? null,
      notes: initialData?.notes ?? null,
      full_name:
          initialData?.recipient_full_name ??
          initialData?.full_name ??
          "",

      country_code:
          initialData?.recipient_country_code ??
          initialData?.country_code ??
          "+966",

      mobile_number:
          initialData?.recipient_mobile_number ??
          initialData?.mobile_number ??
          "",
    },
  });

  const selectedCountry = watch("country_id");
  const selectedCity = watch("city_id");
  const formValues = watch();

  useEffect(() => { dispatch(fetchCountry()); }, [dispatch]);

  useEffect(() => {
    if (initialData) {
      reset({
        country_id: vendorCountryId,
        city_id: vendorCityId,
        delivery_agent_id: initialData?.assigned_to,
        quantity: maxQty,
        amount_deducted_reason: initialData?.amount_deducted_reason,
        amount_deducted: initialData?.amount_deducted,
        delivery_charge: initialData?.delivery_charge ?? null,
        delivery_charge_paid_amount: initialData?.delivery_charge_paid_amount ?? null,
        cash_amount: initialData?.cash_amount ?? null,
        deadline: initialData?.deadline ? formatDateTimeLocal(initialData.deadline) : null,
        priority: initialData?.priority ?? "medium",
        delivery_address: initialData?.delivery_address ?? "",
        delivery_landmark: initialData?.delivery_landmark ?? "",
        latitude: initialData?.latitude ?? null,
        longitude: initialData?.longitude ?? null,
        notes: initialData?.notes ?? null,
        full_name:
            initialData?.recipient_full_name ??
            initialData?.full_name ??
            "",

        country_code:
            initialData?.recipient_country_code ??
            initialData?.country_code ??
            "+966",

        mobile_number:
            initialData?.recipient_mobile_number ??
            initialData?.mobile_number ??
            "",
      });
    }
  }, [initialData, reset, maxQty, vendorCountryId, vendorCityId]);

  useEffect(() => {
    if (selectedCountry) dispatch(fetchCity(selectedCountry));
  }, [selectedCountry, dispatch]);

  useEffect(() => {
    if (selectedCountry && selectedCity) {
      dispatch(fetchDeliveryPartners({ country_id: selectedCountry, city_id: selectedCity }));
    }
  }, [selectedCountry, selectedCity, dispatch]);

  const inputLabelClass = "mb-2 block text-sm font-semibold text-[#1F2937]";
  const commonInputClass = "!border !border-[#CFD2D9] rounded-lg h-[50px] !text-[#111827] bg-gray-100 cursor-not-allowed transition";

  return (
    <Dialog open={true} handler={onClose} size="sm" className="rounded-2xl 2xl:min-w-[40%] 2xl:max-w-[40%] p-0 overflow-hidden shadow-2xl">
      <DialogHeader className="flex justify-between items-center border-b px-8 py-5 bg-white z-10 relative">
        <Typography className="text-xl font-bold text-gray-900">{t('assigned_delivery_agent_title')}</Typography>
        <XMarkIcon className="h-6 w-6 cursor-pointer text-gray-500 hover:text-black transition" onClick={onClose} />
      </DialogHeader>
      {/* This invisible input will take the initial focus instead of the deadline field */}
      <input type="text" style={{ opacity: 0, height: 0, width: 0, position: 'absolute' }} autoFocus />        

      <DialogBody className="p-0 max-h-[70vh] overflow-y-auto">
        <div className="px-8 pt-6 pb-2 space-y-6">
            
          {/* Header Info Block */}
          <div className="p-4 rounded-xl border border-[#E5E7EB] bg-white">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{t('vendor_header')}</p>
                <p className="text-base font-bold text-gray-900 truncate">{vendor?.vendor_name || "N/A"}</p>
              </div>
              <div className="text-right">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{t('quantity_header')}</p>
                <p className="text-base font-bold text-gray-900">{maxQty}</p>
              </div>
            </div>
          </div>

          {/* Location Grid */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={inputLabelClass}>{t('select_country_label')}</label>
              <Controller
                control={control}
                name="country_id"
                render={({ field }) => (
                  <Select {...field} isDisabled options={countries} value={countries.find(c => String(c.value) === String(field.value)) || null} styles={customSelectStyles} />
                )}
              />
            </div>
            <div>
              <label className={inputLabelClass}>{t('select_city_label')}</label>
              <Controller
                control={control}
                name="city_id"
                render={({ field }) => (
                  <Select {...field} isDisabled options={cities} value={cities.find(c => String(c.value) === String(field.value)) || null} styles={customSelectStyles} />
                )}
              />
            </div>
          </div>

          {/* Agent & Timing */}
          <div className="space-y-5">
            <div>
              <label className={inputLabelClass}>{t('select_delivery_agent_label')}</label>
              <Controller
                control={control}
                name="delivery_agent_id"
                render={({ field }) => (
                  <Select {...field} isDisabled options={delivery_partners} value={delivery_partners.find(dp => String(dp.value) === String(field.value)) || null} styles={customSelectStyles} />
                )}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={inputLabelClass}>{t('deadline_date_label')}</label>
                <Input type="datetime-local" readOnly {...register("deadline")} className={commonInputClass} labelProps={{ className: "hidden" }} />
              </div>
              <div>
                <label className={inputLabelClass}>{t('priority_label')}</label>
                <Controller
                  control={control}
                  name="priority"
                  render={({ field }) => (
                    <Select {...field} isDisabled options={priorities} value={priorities.find(p => p.value === field.value)} styles={customSelectStyles} />
                  )}
                />
              </div>
            </div>

            {/* Address Details */}
            <div>
              <label className={inputLabelClass}>{t('delivery_location_label')}</label>
              <DeliveryLocationSearch value={formValues.delivery_address} disabled={true} placeholder="N/A" />
            </div>

            <div>
              <label className={inputLabelClass}>{t('delivery_landmark_label')}</label>
              <DeliveryLocationSearch value={formValues.delivery_landmark} disabled={true} placeholder="N/A" />
            </div>

            <div>
              <label className={inputLabelClass}>{t('note_label')}</label>
              <textarea rows="3" readOnly className="w-full border border-[#CFD2D9] rounded-lg px-4 py-3 outline-none text-sm bg-gray-100 cursor-not-allowed" {...register("notes")} />
            </div>

            <div>
              <label className={inputLabelClass}>{t('recipient_full_name_label')}</label>
              <Input
                type="text"
                readOnly
                {...register("full_name")}
                className={commonInputClass}
                labelProps={{ className: "hidden" }}
              />
            </div>

            <div className="flex flex-wrap sm:flex-nowrap gap-4 sm:gap-6 w-full">
              <div className="w-full sm:w-auto shrink-0">
                <label className={inputLabelClass}>{t('country_code_label')}</label>
                <Input
                  type="text"
                  readOnly
                  {...register("country_code")}
                  className={commonInputClass}
                  labelProps={{ className: "hidden" }}
                />
              </div>
              <div className="w-full sm:w-2/3 min-w-[200px]">
                <label className={inputLabelClass}>{t('recipient_mobile_number_label')}</label>
                <Input type="text" readOnly {...register("mobile_number")} className={commonInputClass} labelProps={{ className: "hidden" }} />
              </div>
            </div>

            {/* Financials */}
            <div>
              <label className={inputLabelClass}>{t('amount_to_be_collected_label')}</label>
              <Input type="number" readOnly {...register("cash_amount")} className={commonInputClass} labelProps={{ className: "hidden" }} />
            </div>

            <div>
              <label className={inputLabelClass}>{t('delivery_charge_label')}</label>
              <Input type="number" readOnly {...register("delivery_charge")} className={commonInputClass} labelProps={{ className: "hidden" }} />
            </div>

            {/* Paid & Deducted Details (Matched to Edit Modal logic) */}
            <div>
              <label className={inputLabelClass}>{t('amount_paid_to_dp_label')}</label>
              <Input type="number" readOnly {...register("delivery_charge_paid_amount")} className={commonInputClass} labelProps={{ className: "hidden" }} />
            </div>

            {initialData?.delivery_charge_paid_amount && (
              <>
                <div>
                  <label className={inputLabelClass}>{t('deducted_amount_label')}</label>
                  <Input type="number" readOnly {...register("amount_deducted")} className={commonInputClass} labelProps={{ className: "hidden" }} />
                </div>
                <div>
                  <label className={inputLabelClass}>{t('deducted_amount_reason_label')}</label>
                  <textarea rows="3" readOnly className="w-full border border-[#CFD2D9] rounded-lg px-4 py-3 outline-none text-sm bg-gray-100 cursor-not-allowed" {...register("amount_deducted_reason")} />
                </div>
              </>
            )}

            {/* Assign Quantity Footer */}
            <div className="pb-6">
              <label className={inputLabelClass}>{t('assign_quantity_label', { maxQty })}</label>
              <Input type="number" readOnly value={maxQty} className={commonInputClass} labelProps={{ className: "hidden" }} />
            </div>
          </div>
        </div>
      </DialogBody>

      <DialogFooter className="border-t flex justify-end px-8 py-5 bg-white z-10 relative">
        <button type="button" onClick={onClose} className="px-9 py-2.5 rounded-lg bg-[#F3F4F6] text-[#374151] font-bold hover:bg-[#E5E7EB] transition-colors">{t('close_button')}</button>
      </DialogFooter>
    </Dialog>
  );
}